import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ChevronRight, Upload, FileText } from 'lucide-react';

// ✅ Config Imports
import { IS_DEV, TEST_REAL_API } from "@/config/env";

// ✅ Validation Imports
import { establishmentProofSchema, ESTABLISHMENT_TYPES } from './doctorValidation';
import type { EstablishmentProofData } from './doctorValidation';

// ✅ API Import
import { saveEstablishment } from '../../../../../api/onboardingApis/doctorOnboardingApi/doctorEstablishment.api';

interface DoctorStep5Props {
  onNext: (data: EstablishmentProofData) => void;
}

export default function DoctorStep5({ onNext }: DoctorStep5Props) {
  const [fileName, setFileName] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    clearErrors,
    trigger, // 🔥 Added trigger
  } = useForm<EstablishmentProofData>({
    resolver: zodResolver(establishmentProofSchema),
    defaultValues: {
      isAnonymous: false,
      establishmentType: undefined,
      hospitalName: "",
    },
    mode: "all",
    shouldUnregister: true, 
  });

  const isAnonymous = watch("isAnonymous");

  // 🧹 Effect: Register the file field manually
  useEffect(() => {
    register("establishmentProof");
  }, [register]);

  // 🧹 Effect: Clear errors when toggling Anonymous
  useEffect(() => {
    if (isAnonymous) {
      clearErrors(); 
    }
  }, [isAnonymous, clearErrors]);

  // 🟢 SUBMIT HANDLER
  const onSubmit = async (data: EstablishmentProofData) => {
    setIsSubmitting(true);
    try {
      if (IS_DEV && !TEST_REAL_API) {
         // DEV SKIP
         await new Promise(resolve => setTimeout(resolve, 800));
         const mockData: EstablishmentProofData = {
            isAnonymous: false,
            establishmentType: 'Owner of establishment',
            hospitalName: 'Apollo Hospital',
            establishmentProof: new DataTransfer().files
         };
         toast.success("⚡ Mock Establishment Proof Saved (Dev Mode)");
         onNext(mockData);
         setIsSubmitting(false);
         return; 
      }

   
      const expertCode = localStorage.getItem('expertCode');
      
      if (!expertCode) {
        toast.error("Session missing. Please login again.");
        setIsSubmitting(false);
        return;
      }

      const formData = new FormData();
      formData.append('expertCode', expertCode);
      formData.append('isAnonymous', String(data.isAnonymous));

      // Only append details if NOT anonymous
      if (!data.isAnonymous) {
         formData.append('establishmentType', data.establishmentType || '');
         formData.append('hospitalName', data.hospitalName || '');
         
         if (data.establishmentProof && data.establishmentProof.length > 0) {
           formData.append('file', data.establishmentProof[0]); 
         }
      }

      await saveEstablishment(formData);

      toast.success("Establishment details saved!");
      onNext(data);

    } catch (error: any) {
      console.error("API Error:", error);
      toast.error(error.response?.data?.message || "Failed to save details. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Handlers ---
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setFileName(files[0].name);
      
      // 🔥 FIX: Set value and Force Validation
      setValue("establishmentProof", files, { shouldValidate: true });
      trigger("establishmentProof"); 
      clearErrors("establishmentProof");
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setFileName(files[0].name);
      
      // 🔥 FIX: Set value and Force Validation
      setValue("establishmentProof", files, { shouldValidate: true });
      trigger("establishmentProof");
      clearErrors("establishmentProof");
    }
  };

  const isStrictMode = !IS_DEV || TEST_REAL_API;

  return (
    <div className="bg-white rounded-lg shadow-xl p-8">
      
      <div className="flex justify-between items-start mb-6">
        <div>
           <h2 className="text-2xl font-bold text-gray-800 mb-2">Establishment Proof</h2>
           <p className="text-gray-600">Provide details about your workplace</p>
        </div>
        
        {TEST_REAL_API && IS_DEV && (
          <div className="bg-yellow-500/20 text-yellow-700 py-1 px-3 rounded-full text-xs border border-yellow-500/50 font-medium">
            ⚠️ Real API Testing Enabled
          </div>
        )}
      </div>

      {/* 🟢 ANONYMOUS CHECKBOX */}
      <div 
        onClick={() => {
            setValue("isAnonymous", !isAnonymous);
            trigger();
        }}
        className={`
            mb-8 p-4 border rounded-lg flex items-center gap-3 cursor-pointer transition-colors select-none
            ${isAnonymous ? 'bg-cyan-50 border-cyan-500' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}
        `}
      >
        <div className={`
            w-5 h-5 rounded border flex items-center justify-center
            ${isAnonymous ? 'bg-cyan-500 border-cyan-500' : 'bg-white border-gray-400'}
        `}>
            {isAnonymous && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>}
        </div>
        <input 
            type="checkbox"
            {...register("isAnonymous")}
            className="hidden" 
        />
        <label className="text-gray-700 font-medium cursor-pointer flex-1">
            I want to keep Anonymous
        </label>
      </div>

      {/* 🟢 CONDITIONAL RENDERING */}
      {!isAnonymous ? (
        <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-300">
          
          {/* 1. Establishment Type */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-3">
               I am <span className="text-red-500">*</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ESTABLISHMENT_TYPES.map((type) => (
                <label key={type} className={`
                    flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all
                    ${watch('establishmentType') === type ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-gray-300'}
                `}>
                  <input
                    type="radio"
                    {...register("establishmentType")}
                    value={type}
                    className="w-4 h-4 text-cyan-600 focus:ring-cyan-500"
                  />
                  <span className="text-gray-700 text-sm">{type}</span>
                </label>
              ))}
            </div>
            {errors.establishmentType && <p className="mt-2 text-sm text-red-600">{errors.establishmentType.message}</p>}
          </div>

          {/* 2. Hospital Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
               Hospital/Clinic Name <span className="text-red-500">*</span>
            </label>
            <input
               type="text"
               {...register("hospitalName")}
               placeholder="Enter name of your establishment"
               className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                   errors.hospitalName ? 'border-red-500' : 'border-gray-300'
               }`}
            />
            {errors.hospitalName && <p className="mt-2 text-sm text-red-600">{errors.hospitalName.message}</p>}
          </div>

          {/* 3. Proof Upload */}
          <div>
            <div className="mb-2">
               <p className="font-semibold text-gray-700">Accepted proofs (Any one) <span className="text-red-500">*</span></p>
               <p className="text-xs text-gray-500">Clinic Registration Proof / Waste Disposal Proof / Tax receipt</p>
            </div>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                isDragging 
                    ? 'border-cyan-500 bg-cyan-50' 
                    : errors.establishmentProof ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-cyan-400 bg-white'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              {/* 🔥 FIX: Removed ...register() here to fix file validation conflict */}
              <input
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                id="establishment-upload"
              />
              <label htmlFor="establishment-upload" className="cursor-pointer w-full h-full block">
                <div className="flex flex-col items-center">
                  {fileName ? (
                    <>
                       <FileText className="w-12 h-12 text-cyan-500 mb-3" />
                       <p className="text-sm font-medium text-gray-700">{fileName}</p>
                       <p className="text-xs text-gray-500 mt-1">Click to replace</p>
                    </>
                  ) : (
                    <>
                       <Upload className="w-12 h-12 text-gray-400 mb-3" />
                       <div className="bg-white border border-gray-300 text-cyan-500 px-6 py-2 rounded-lg mb-2 shadow-sm">
                          Upload Proof
                       </div>
                       <p className="text-xs text-gray-500">PDF, JPG, PNG (Max 5MB)</p>
                    </>
                  )}
                </div>
              </label>
            </div>
            {errors.establishmentProof && <p className="mt-2 text-sm text-red-600">{errors.establishmentProof.message as string}</p>}
          </div>
        </div>
      ) : (
        <div className="p-6 bg-cyan-50 border border-cyan-100 rounded-lg text-center animate-in fade-in zoom-in-95 duration-200">
           <div className="w-12 h-12 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
           </div>
           <h3 className="text-lg font-medium text-gray-800">Anonymous Mode Enabled</h3>
           <p className="text-gray-600 mt-1 text-sm">You have chosen not to disclose your establishment details publicly.</p>
        </div>
      )}

      {/* SUBMIT BUTTON */}
      <div className="text-center mt-10">
        <button
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting || (isStrictMode && !isAnonymous && !isValid)}
          className={`w-full bg-cyan-400 hover:bg-cyan-500 text-white font-semibold py-3 px-12 rounded-lg transition-colors 
             ${(isSubmitting || (isStrictMode && !isAnonymous && !isValid)) ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isSubmitting ? "Processing..." : (
             <span className="flex items-center justify-center gap-2">
               {isAnonymous ? "Complete Step" : "Proceed to Final Step"} <ChevronRight size={20} />
             </span>
          )}
        </button>
      </div>
    </div>
  );
}